import React, { useEffect, useMemo, useRef, useState } from "react";
import { CallAiActions } from "@/components/ai/CallAiActions";
import { AiIcon } from "@/components/ai/AiIcon";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, Check, Flag, Loader2, Pencil, Phone, Play, Download } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListPager } from "@/components/ui/list-pager";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { FilterBar } from "@/components/filters/FilterBar";
import { ListEmpty, PageHeader, StatusPill, ViewsPanel, initialsOf, type Tone } from "@/components/patterns";
import { CallPlayer } from "@/components/calling/CallPlayer";
import { useSoftphone } from "@/components/calling/SoftphoneContext";
import { LinkCaller } from "@/components/calling/LinkCaller";
import { CallOutcomeDialog, type OutcomeTarget } from "@/components/calling/CallOutcomeDialog";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { downloadCsv, fetchAllRows } from "@/lib/exportCsv";
import { dateRangeBounds, toQuery } from "@/lib/filters";
import { cn } from "@/lib/utils";
import { Calendar as FilterDateIcon, ArrowDownUp as FilterDirIcon, ListChecks as FilterOutcomeIcon, UserCircle2 as FilterRepIcon } from "lucide-react";
import { dateLocale, withClock } from "@/lib/dateTime";

interface CallRow {
  id: string;
  user_id: string;
  rep_name: string;
  related_type: "LEAD" | "CONTACT" | "DEAL" | "ACCOUNT" | "UNKNOWN";
  related_id: string | null;
  person: string;
  to_number: string;
  direction: "OUTBOUND" | "INBOUND";
  call_status: string;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  duration_sec: number | null;
  hangup_cause: string | null;
  outcome: string | null;
  note: string | null;
  has_recording: boolean;
  recording_status: string | null;
  review_result: string | null;
  review_note: string | null;
  voicemail: boolean;
  routed_to: string | null;
  route_steps: Array<Record<string, any>> | null;
  handled_at: string | null;
  match_conflict: boolean;
  callback_due: string | null;
}
interface Stats {
  scope: "all" | "team" | "own";
  counts: { all: number; answered: number; missed: number; needs_outcome: number; with_recording: number; callback: number; voicemail: number; overdue: number };
  today: { calls: number; talk_sec: number; answered: number };
  reps: { id: string; name: string }[];
}

const OUTCOME: Record<string, { label: string; tone: Tone }> = {
  INTERESTED: { label: "Interested", tone: "success" },
  CALL_BACK: { label: "Call back later", tone: "info" },
  NOT_INTERESTED: { label: "Not interested", tone: "neutral" },
  NO_ANSWER: { label: "No answer", tone: "warning" },
  BUSY: { label: "Busy", tone: "warning" },
  VOICEMAIL: { label: "Voicemail", tone: "neutral" },
  WRONG_NUMBER: { label: "Wrong number", tone: "danger" },
  DO_NOT_CALL: { label: "Asked not to call", tone: "danger" },
};
const REVIEW: Record<string, string> = { FINE: "Looks fine", COACHING: "Needs coaching", WRONG_OUTCOME: "Wrong outcome" };
const LINK: Record<string, string> = { LEAD: "/leads", CONTACT: "/contacts", DEAL: "/deals", ACCOUNT: "/accounts" };
const KIND: Record<string, string> = { LEAD: "Lead", CONTACT: "Contact", DEAL: "Deal", ACCOUNT: "Account" };

const FILTER_DEFAULTS = { rep: "all", direction: "all", outcome: "all", when: "any" };
const OUTCOME_OPTIONS = [{ value: "all", label: "Any outcome" }, ...Object.entries(OUTCOME).map(([value, o]) => ({ value, label: o.label }))];

const answered = (c: CallRow) => c.call_status === "ANSWERED" || c.call_status === "COMPLETED";
const clock = (s: number | null) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
const longTime = (s: number) => (s >= 3600 ? `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min` : `${Math.floor(s / 60)} min`);
const dayOf = (iso: string) => {
  const d = new Date(iso);
  const diff = Math.round((new Date(new Date().toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86400000);
  return diff === 0 ? "Today" : diff === 1 ? "Yesterday" : d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
};
const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(dateLocale(), withClock({ hour: "numeric", minute: "2-digit" }));
const num = (n: string) => (n.length > 10 ? `+${n.slice(0, n.length - 10)} ${n.slice(-10, -5)} ${n.slice(-5)}` : n);

/** Every call within your reach, with recordings: your own (Sales executive), your team's (Sales head) or everyone's (Super admin) */
export function CallsPage() {
  const { user, hasPermission } = useAuth();
  const { success, error } = useToast();
  const [params, setParams] = useSearchParams();
  const [view, setViewState] = useState(params.get("view") || "all");
  const target = params.get("target") || "";
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState<Stats | null>(null);
  const [open, setOpen] = useState<CallRow | null>(null);
  const [outcome, setOutcome] = useState<OutcomeTarget | null>(null);
  const [exporting, setExporting] = useState(false);
  const filters = usePersistentFilters("calls", FILTER_DEFAULTS);
  const canReview = hasPermission("report.view_team");

  const setView = (v: string) => {
    setViewState(v);
    const next = new URLSearchParams(params);
    if (v === "all") next.delete("view");
    else next.set("view", v);
    setParams(next, { replace: true });
  };

  const queryString = (extra: Record<string, string | number> = {}) => {
    const f = filters.values;
    const b = dateRangeBounds(f.when);
    return toQuery({
      search: search.trim(),
      view: view === "all" ? "" : view,
      user_id: f.rep,
      direction: f.direction,
      outcome: f.outcome,
      from: b?.[0]?.toISOString(),
      to: b?.[1]?.toISOString(),
      target_id: target,
      ...extra,
    });
  };

  const list = usePagedList<CallRow>({
    deps: [filters.values, search, view, target],
    debounceMs: 250,
    load: async (page) => unwrapPage<CallRow>(await api.get(`/api/Call/List${queryString({ page })}`)),
  });

  const loadStats = () =>
    api
      .get(`/api/Call/Stats${queryString({ view: "" })}`)
      .then((r) => setStats(unwrapObject<Stats>(r)))
      .catch(() => undefined);
  useEffect(() => {
    void loadStats();
  }, [filters.values, search, target]);

  const refresh = async () => {
    await Promise.all([list.reload(), loadStats()]);
  };

  const scope = stats?.scope || "own";
  const title = scope === "all" ? "All calls" : scope === "team" ? "Your team's calls" : "Your calls";
  const blurb = scope === "all" ? "Everything the team has dialled or answered, with recordings." : scope === "team" ? "Calls by you and your team, with recordings." : "Calls you made or took, with your recordings and outcomes.";
  const c = stats?.counts;
  const filtered = !!search.trim() || view !== "all" || filters.activeCount > 0 || !!target;

  const exportAll = async () => {
    setExporting(true);
    try {
      const { rows } = await fetchAllRows<CallRow>("/api/Call/List", queryString());
      downloadCsv<CallRow>(`calls-${new Date().toISOString().slice(0, 10)}.csv`, [
        ["When", (r) => new Date(r.started_at).toLocaleString(dateLocale(), withClock())],
        ["Rep", (r) => r.rep_name],
        ["Person called", (r) => r.person],
        ["Type", (r) => KIND[r.related_type] || r.related_type],
        ["Number", (r) => r.to_number],
        ["Direction", (r) => (r.direction === "INBOUND" ? "Incoming" : "Outgoing")],
        ["Seconds", (r) => r.duration_sec ?? ""],
        ["Outcome", (r) => (r.outcome ? OUTCOME[r.outcome]?.label || r.outcome : "")],
        ["Recording", (r) => (r.has_recording ? "yes" : "no")],
      ], rows);
    } catch (err: any) {
      error("Could not export", err.message || "Please try again");
    } finally {
      setExporting(false);
    }
  };

  const sp = useSoftphone();
  // A call came in, was missed, handled or ended: the list and the counts follow without a reload
  const refreshRef = useRef<() => Promise<void>>(async () => undefined);
  useEffect(() => { refreshRef.current = refresh; });
  const firstTick = useRef(sp.callsTick);
  useEffect(() => {
    if (sp.callsTick === firstTick.current) return;
    void refreshRef.current();
  }, [sp.callsTick]);
  const markHandled = async (row: CallRow) => {
    try {
      await api.post(`/api/Call/Handled/${row.id}`);
      success("Marked as handled", "The callback task is closed.");
      setOpen({ ...row, handled_at: new Date().toISOString() });
      await refresh();
    } catch (err: any) {
      error("Could not mark it handled", err.message || "Please try again");
    }
  };
  const callBack = async (row: CallRow) => {
    if (!row.related_id || row.related_type === "UNKNOWN") return;
    try {
      await sp.startCall({ type: row.related_type, id: row.related_id });
      setOpen(null);
      void refresh();
    } catch (err: any) {
      error("Could not start the call", err.message || "Please try again");
    }
  };

  const review = async (row: CallRow, result: "FINE" | "COACHING") => {
    try {
      await api.post(`/api/Report/CallsReview/${row.id}`, { result });
      success("Review saved", REVIEW[result]);
      setOpen({ ...row, review_result: result });
      await refresh();
    } catch (err: any) {
      error("Could not save the review", err.message || "Please try again");
    }
  };

  const repOptions = useMemo(() => [{ value: "all", label: "Any rep" }, ...(stats?.reps || []).map((r) => ({ value: r.id, label: r.id === user?.Id ? `${r.name} (you)` : r.name }))], [stats, user?.Id]);

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Calls"
        subtitle={scope === "all" ? "Scope: everyone" : scope === "team" ? "Scope: your team" : "Scope: only you"}
        active={view}
        onSelect={setView}
        groups={[
          {
            items: [
              { key: "all", label: "All calls", count: c?.all },
              { key: "answered", label: "Answered", count: c?.answered },
              { key: "callback", label: "Missed · call back", count: c?.callback, tone: "warning" },
              { key: "voicemail", label: "Voicemail", count: c?.voicemail },
              { key: "missed", label: "Missed", count: c?.missed },
              { key: "needs_outcome", label: "Needs an outcome", count: c?.needs_outcome, tone: "warning" },
              { key: "with_recording", label: "With recording", count: c?.with_recording },
            ],
          },
        ]}
        footer={
          stats && (
            <div className="mt-3 flex flex-col gap-1.5 px-2.5 text-[12.5px]">
              <div className="pb-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Today</div>
              <div className="flex justify-between"><span>Calls made</span><strong className="font-mono">{stats.today.calls}</strong></div>
              <div className="flex justify-between"><span>Talk time</span><strong className="font-mono">{longTime(stats.today.talk_sec)}</strong></div>
              <div className="flex justify-between"><span>Answered</span><strong className="font-mono">{stats.today.answered}</strong></div>
            </div>
          )
        }
      />

      <PageHeader
        title={title}
        summary={list.total}
        actions={
          <>
            {scope !== "own" && (
              <Button variant="outline" size="sm" onClick={exportAll} disabled={exporting}>
                {exporting ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Download className="mr-1.5 size-4" />}
                Export CSV
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings/my-calling">Test call</Link>
            </Button>
          </>
        }
      />
      <p className="text-[13px] text-muted-foreground">{blurb}</p>

      {view === "callback" && (c?.overdue || 0) > 0 && (
        <div role="status" className="flex items-center gap-2 rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-destructive">
          <Phone className="size-4 shrink-0" />
          <span><strong>{c?.overdue} {c?.overdue === 1 ? "callback is" : "callbacks are"} overdue.</strong> Overdue ones are first in the list, and the owners and their heads have been told.</span>
        </div>
      )}

      {target && (
        <div className="flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-[13px] text-accent-foreground">
          Showing the calls for one record.
          <button type="button" onClick={() => { const n = new URLSearchParams(params); n.delete("target"); setParams(n, { replace: true }); }} className="font-semibold underline">Show all calls</button>
        </div>
      )}

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search name or number" }}
        fields={[
          { name: "rep", label: "Rep", icon: FilterRepIcon, type: "select", options: repOptions, defaultValue: "all", hidden: scope === "own" },
          { name: "direction", label: "Direction", icon: FilterDirIcon, type: "select", options: [{ value: "all", label: "Any direction" }, { value: "OUTBOUND", label: "Outgoing" }, { value: "INBOUND", label: "Incoming" }], defaultValue: "all" },
          { name: "outcome", label: "Outcome", icon: FilterOutcomeIcon, type: "select", options: OUTCOME_OPTIONS, defaultValue: "all" },
          { name: "when", label: "When", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={filters.values}
        onChange={(n, v) => filters.set(n as keyof typeof FILTER_DEFAULTS, v)}
        onReset={() => filters.reset()}
        shown={list.total}
        total={list.total}
        noun="calls"
      />

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={7} columns={6} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border bg-panel hover:bg-panel">
                <TableHead className="pl-3.5">When</TableHead>
                {scope !== "own" && <TableHead>Rep</TableHead>}
                <TableHead>Person called</TableHead>
                <TableHead>Number</TableHead>
                <TableHead>Direction</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Recording</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    <ListEmpty
                      noun="calls"
                      icon={Phone}
                      error={list.error}
                      onRetry={list.reload}
                      filtered={filtered}
                      onClear={() => { filters.reset(); setSearch(""); setView("all"); const n = new URLSearchParams(params); n.delete("target"); setParams(n, { replace: true }); }}
                      firstUse="Calls you make from a lead, contact, account or deal appear here, with their recordings."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                list.rows.map((r) => {
                  const o = r.outcome ? OUTCOME[r.outcome] : null;
                  const ok = answered(r);
                  return (
                    <TableRow key={r.id} onClick={() => setOpen(r)} className="cursor-pointer">
                      <TableCell className="whitespace-nowrap pl-3.5">
                        <span className="block font-medium">{dayOf(r.started_at)}</span>
                        <span className="text-[11.5px] text-muted-foreground">{timeOf(r.started_at)}</span>
                      </TableCell>
                      {scope !== "own" && (
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="flex size-[22px] items-center justify-center rounded-full bg-accent text-[9.5px] font-bold text-accent-foreground">{initialsOf(r.rep_name)}</span>
                            {r.rep_name}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>
                        {r.related_id && LINK[r.related_type] ? (
                          <Link to={`${LINK[r.related_type]}/${r.related_id}`} onClick={(e) => e.stopPropagation()} className="font-semibold text-primary hover:underline">{r.person}</Link>
                        ) : (
                          <span className="font-semibold">{r.person}</span>
                        )}
                        <span className="block text-[11.5px] text-muted-foreground">{KIND[r.related_type] || "No record found"}{r.match_conflict ? " · same number on 2 records" : ""}</span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{num(r.to_number)}</TableCell>
                      <TableCell>
                        <span className={cn("inline-flex items-center gap-1.5", ok ? "text-success" : "text-muted-foreground")}>
                          {r.direction === "INBOUND" ? <ArrowDownLeft className="size-3.5" /> : <ArrowUpRight className="size-3.5" />}
                          {r.direction === "INBOUND" ? "Incoming" : "Outgoing"}
                          {ok && r.duration_sec ? <span className="font-mono text-[11.5px] text-muted-foreground">· {clock(r.duration_sec)}</span> : !ok ? <span className="text-[11.5px]">· {r.direction === "INBOUND" ? "missed" : "not answered"}</span> : null}
                        </span>
                      </TableCell>
                      <TableCell>{o ? <StatusPill tone={o.tone}>{o.label}</StatusPill> : r.direction === "INBOUND" && !ok && r.call_status !== "INITIATED" && r.call_status !== "RINGING" ? <CallbackPill row={r} /> : r.direction === "INBOUND" && !ok ? <StatusPill tone="neutral">Ringing</StatusPill> : r.call_status === "INITIATED" || r.call_status === "RINGING" ? <StatusPill tone="neutral">In progress</StatusPill> : <StatusPill tone="warning">Needs outcome</StatusPill>}</TableCell>
                      <TableCell>
                        {r.has_recording ? (
                          <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2.5 text-xs" onClick={(e) => { e.stopPropagation(); setOpen(r); }}>
                            <Play className="size-3" /> {r.voicemail ? "Voicemail" : "Play"}{r.duration_sec ? <span className="font-mono text-muted-foreground">{clock(r.duration_sec)}</span> : null}
                          </Button>
                        ) : r.voicemail && r.recording_status === "PENDING" ? (
                          <StatusPill tone="warning">Voicemail processing</StatusPill>
                        ) : r.recording_status === "PENDING" ? (
                          <StatusPill tone="warning">Processing</StatusPill>
                        ) : (
                          <span className="text-xs text-muted-foreground">No recording</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[420px]">
          {open && <Details row={open} mine={open.user_id === user?.Id} canReview={canReview} onReview={review} onHandled={markHandled} onCallBack={callBack} canCall={sp.phase === "ready"} onLinked={(r) => { setOpen({ ...open!, related_type: r.type, related_id: r.id, person: r.label }); void refresh(); }} onEditOutcome={() => setOutcome({ callId: open.id, label: open.person, sub: num(open.to_number) })} />}
        </SheetContent>
      </Sheet>
      <CallOutcomeDialog
        target={outcome}
        onClose={(saved) => {
          setOutcome(null);
          if (saved) { setOpen(null); void refresh(); }
        }}
      />
    </div>
  );
}

function CallbackPill({ row }: { row: CallRow }) {
  if (row.handled_at) return <StatusPill tone="success">Handled</StatusPill>;
  if (!row.callback_due) return <StatusPill tone="warning">Call back</StatusPill>;
  const mins = Math.round((new Date(row.callback_due).getTime() - Date.now()) / 60000);
  const text = mins >= 0 ? `Due in ${mins >= 60 ? `${Math.floor(mins / 60)} h` : `${mins} min`}` : `Overdue ${-mins >= 60 ? `${Math.floor(-mins / 60)} h` : `${-mins} min`}`;
  return <StatusPill tone={mins >= 0 ? "warning" : "danger"}>{text}</StatusPill>;
}

const Box = ({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) => (
  <section className="flex flex-col gap-2 rounded-xl border border-border bg-card px-3.5 py-3">
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

function Details({ row, mine, canReview, onReview, onHandled, onCallBack, canCall, onLinked, onEditOutcome }: { row: CallRow; mine: boolean; canReview: boolean; onReview: (r: CallRow, result: "FINE" | "COACHING") => void; onHandled: (r: CallRow) => void; onCallBack: (r: CallRow) => void; canCall: boolean; onLinked: (r: { type: "LEAD" | "CONTACT"; id: string; label: string }) => void; onEditOutcome: () => void }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [row.id]);
  const o = row.outcome ? OUTCOME[row.outcome] : null;
  const ok = answered(row);
  const inbound = row.direction === "INBOUND";
  const missedIn = inbound && !ok;
  const STAGE: Record<string, string> = { OWNER: "the owner", TEAM: "the team", OVERFLOW: "the overflow group" };
  const routed = (row.route_steps || []).flatMap((st) => {
    const at = st.at || row.started_at;
    if (st.kind === "match") return [{ at, text: st.record ? `Matched ${st.record.label} (${KIND[st.record.type] || st.record.type}). ${st.reason}` : `${st.reason}` }];
    if (st.kind === "state") {
      const WORD: Record<string, string> = { AVAILABLE: "was available", ON_CALL: "was on a call", WRAP_UP: "was in wrap-up", AWAY: "was away", OFFLINE: "was offline" };
      return (st.owners || []).map((o: any) => ({ at, text: `${o.name || "The owner"} ${WORD[o.status] || o.status}` }));
    }
    if (st.kind === "declined") {
      const who = (row.route_steps || []).filter((x) => x.kind === "ring").flatMap((x) => x.people || []).find((p: any) => p.id === st.by)?.name;
      return [{ at, text: `Declined${who ? ` by ${who}` : ""}` }];
    }
    if (st.kind === "stop") return [{ at, text: st.reason === "DECLINED" ? "Not passed on: sent to the owner's voicemail" : "No answer: sent to the owner's voicemail, not passed on" }];
    if (st.kind === "ring") return [{ at, text: `Rang ${STAGE[st.stage] || st.stage}: ${(st.people || []).map((p: any) => p.name).join(", ")}` }];
    if (st.kind === "skip") return [{ at, text: `Skipped ${STAGE[st.stage] || st.stage} (${String(st.reason || "").toLowerCase()})` }];
    if (st.kind === "result") return [{ at, text: st.answered ? "Answered" : `No answer (${st.status || "no-answer"}${st.reason || st.cause ? `: ${String(st.reason || st.cause).toLowerCase().replace(/_/g, " ")}` : ""})` }];
    if (st.kind === "voicemail") return [{ at, text: st.enabled ? `Went to voicemail (${String(st.reason || "").toLowerCase()})` : `Nobody could take it (${String(st.reason || "").toLowerCase()})` }];
    if (st.kind === "abandoned") return [{ at, text: "The caller hung up while it rang" }];
    return [];
  });
  const steps = inbound && routed.length ? routed : [
    { at: row.started_at, text: row.direction === "INBOUND" ? "Call came in" : "Dialled from the browser" },
    ...(row.answered_at ? [{ at: row.answered_at, text: ok ? `${row.person} answered` : "Answered" }] : []),
    ...(row.ended_at ? [{ at: row.ended_at, text: ok ? "Call ended" : `Not answered${row.hangup_cause ? ` (${row.hangup_cause.toLowerCase().replace(/_/g, " ")})` : ""}` }] : []),
  ];
  const t = (iso: string) => new Date(iso).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit", second: "2-digit" }));
  return (
    <>
      <SheetHeader className="border-b border-border px-[18px] py-3.5 text-left">
        <SheetTitle className="text-[15px]">{missedIn ? "Missed call from" : inbound ? "Call from" : "Call with"} {row.person}</SheetTitle>
        <SheetDescription className="text-xs">
          {new Date(row.started_at).toLocaleString(dateLocale(), withClock({ day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }))} · {row.direction === "INBOUND" ? "incoming" : "outgoing"}
          {ok && row.duration_sec ? ` · ${Math.floor(row.duration_sec / 60)} min ${String(row.duration_sec % 60).padStart(2, "0")} s` : missedIn ? (row.voicemail ? " · voicemail" : " · missed") : " · not answered"}
        </SheetDescription>
      </SheetHeader>
      <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] py-3.5">
        {missedIn && (
          <Box title="Callback" aside={<CallbackPill row={row} />}>
            <p className="text-[13px] text-muted-foreground">{row.handled_at ? "Someone called back or marked this as dealt with." : "A callback task was made for the owner. It closes when anyone calls this number back."}</p>
            <div className="flex flex-wrap gap-2">
              {row.related_id && row.related_type !== "UNKNOWN" && !row.handled_at && (
                <Button size="sm" onClick={() => onCallBack(row)} disabled={!canCall}>
                  <Phone className="mr-1.5 size-3.5" />
                  Call back now
                </Button>
              )}
              {!row.handled_at && (
                <Button variant="outline" size="sm" onClick={() => onHandled(row)}>
                  <Check className="mr-1.5 size-3.5" />
                  Mark handled
                </Button>
              )}
            </div>
            {!row.related_id && <p className="text-xs text-muted-foreground">We don't know this number yet. Add it to a lead or contact so next time it rings its owner.</p>}
          </Box>
        )}

        {inbound && row.related_type === "UNKNOWN" && (
          <Box title="Link to a record">
            <LinkCaller callId={row.id} onLinked={(r) => onLinked(r)} />
          </Box>
        )}

        <Box title={row.voicemail ? "Voicemail" : "Recording"}>
          {row.has_recording && !failed ? (
            <>
              <CallPlayer callId={row.id} onError={() => setFailed(true)} />
              <p className="text-[11.5px] text-muted-foreground">Every play is written to the security log.</p>
            </>
          ) : failed ? (
            <p className="text-[13px] text-muted-foreground">The recording is no longer available.</p>
          ) : (
            <p className="text-[13px] text-muted-foreground">{row.recording_status === "PENDING" ? "The recording is still being processed. Check again in a few minutes." : row.voicemail ? "The caller didn't leave a message." : "This call wasn't recorded."}</p>
          )}
        </Box>

        {row.has_recording && (
          <Box title="Transcript & summary" aside={<AiIcon className="size-4" />}>
            <CallAiActions callId={row.id} canRedo={canReview} />
          </Box>
        )}

        {!missedIn && (
        <Box title="Outcome">
          <div className="flex flex-wrap gap-1.5">{o ? <StatusPill tone={o.tone}>{o.label}</StatusPill> : <StatusPill tone="warning">Needs an outcome</StatusPill>}</div>
          {row.note && <p className="rounded-lg bg-muted/60 px-2.5 py-2 text-[13px] text-foreground">{row.note}</p>}
          {mine && (
            <Button variant="outline" size="sm" className="w-fit" onClick={onEditOutcome}>
              <Pencil className="mr-1.5 size-3.5" />
              {o ? "Edit outcome" : "Add outcome"}
            </Button>
          )}
        </Box>
        )}

        <Box title="About">
          <div className="flex items-center gap-2.5">
            <span className="flex size-[30px] items-center justify-center rounded-full bg-accent text-[11px] font-bold text-accent-foreground">{initialsOf(row.person)}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold">{row.person}</span>
              <span className="text-xs text-muted-foreground">{KIND[row.related_type] || "No record found"}</span>
            </span>
            {row.related_id && LINK[row.related_type] && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`${LINK[row.related_type]}/${row.related_id}`}>Open {(KIND[row.related_type] || "record").toLowerCase()}</Link>
              </Button>
            )}
          </div>
          <div className="flex justify-between text-[13px]"><span className="text-muted-foreground">Rep</span><span>{row.rep_name}</span></div>
          <div className="flex justify-between text-[13px]"><span className="text-muted-foreground">Number</span><span className="font-mono">{num(row.to_number)}</span></div>
        </Box>

        <Box title={inbound ? "Why it rang this way" : "Timeline"}>
          {steps.map((s) => (
            <div key={s.text} className="flex items-start gap-2.5 text-[13px]">
              <span className="mt-1.5 size-2 shrink-0 rounded-full bg-border" />
              <span className="flex-1">{s.text}</span>
              <span className="font-mono text-[11.5px] text-muted-foreground">{t(s.at)}</span>
            </div>
          ))}
        </Box>

        {canReview && (
          <Box title="Review" aside={<StatusPill tone={row.review_result ? "info" : "neutral"}>{row.review_result ? REVIEW[row.review_result] || row.review_result : "Not reviewed"}</StatusPill>}>
            <p className="text-[12.5px] text-muted-foreground">Mark a call as fine, or as needing coaching. It shows up in the Calls report.</p>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" onClick={() => onReview(row, "FINE")}><Check className="mr-1.5 size-3.5" />Looks fine</Button>
              <Button variant="outline" size="sm" onClick={() => onReview(row, "COACHING")}><Flag className="mr-1.5 size-3.5" />Needs coaching</Button>
            </div>
          </Box>
        )}
      </div>
    </>
  );
}
